/// <reference lib="dom" />
/**
 * Tileset tool: browse and edit the terrain styles (hand-linked tile cases, learned autotile rules, fill, dark tones,
 * decorations, directions and defaults). Edits are stored as a per-style override
 * (saveAsset('terrain-style', id)) and applied to terrain generation immediately.
 *
 * Written with createElement (no JSX) because the node tool tsconfig type-checks src/terrain too.
 */
import { Fragment, createElement as h, useEffect, useMemo, useRef, useState } from 'react';
import { FOLIAGE_KINDS, FOLIAGE_LABELS, type DecorationSettings, type FoliageKind, type LevelDoc, type PaletteEntry, type ShadingSettings, type TerrainShape, type TileLayer, type Vec2 } from '../model/format.ts';
import { saveAsset } from '../data/remoteStore.ts';
import { generateTerrain, onTerrainStylesChanged, setStyleOverride, styleData, styleOverride, terrainData } from './index.ts';
import { EMPTY, FULL, SHAPES } from './shapes.ts';
import { ALL_DIRECTIONS, type Directions, type Stamp, type StyleOverride, type TerrainStyleData } from './types.ts';
import { Btn, ChoiceList, ChoiceThumb, Num, SpritePicker, TT_CSS, Toggle, choiceLabel, drawSpriteAt, pairs, shapePoly, spriteTable, useCanvas, variantPe } from './ttShared.ts';
import { CasesTab } from './CasesTab.ts';

type RuleLayer = 'level' | 'fg' | 'tones';
type Tab = 'cases' | 'rules' | 'fill' | 'tones' | 'decor' | 'settings';

const PAGE = 36;

const NB_POS: [number, number][] = [[-1, 0], [1, 0], [0, -1], [0, 1], [-1, -1], [1, -1], [-1, 1], [1, 1]];

interface CellView {
  /** exact shape */
  shape?: number;
  /** side contact code 0..2 for side cells */
  contact?: number;
  /** corner cell covers the touching corner */
  corner?: boolean;
}

/** Decode a context key (keys.ts) into what is known about the 3x3 neighbourhood. */
function decodeKey(key: string): { own: number; nb: CellView[]; level: number } {
  const id = (c: string) => c.charCodeAt(0) - 97;
  const lv = key.charCodeAt(0) - 97;
  const own = id(key[1]);
  const nb: CellView[] = new Array(8).fill(null).map(() => ({}));
  if (lv === 0) for (let i = 0; i < 8; i++) nb[i].shape = id(key[2 + i]);
  if (lv === 1) {
    for (let i = 0; i < 4; i++) nb[i].shape = id(key[2 + i]);
    const bits = id(key[6]);
    for (let i = 0; i < 4; i++) nb[4 + i].corner = !!(bits & (1 << i));
  }
  if (lv === 2 || lv === 3) {
    for (let i = 0; i < 4; i++) nb[i].contact = +key[2 + i];
    if (lv === 2) {
      const bits = id(key[6]);
      for (let i = 0; i < 4; i++) nb[4 + i].corner = !!(bits & (1 << i));
    }
  }
  return { own, nb, level: lv };
}

const INTERIOR_KEYS = new Set(['abbbbbbbbb', 'bbbbbbp', 'cb2222p']);

const KEY_LEVEL_NAMES = ['exact 3×3', 'sides + corners', 'contacts + corners', 'contacts', 'own shape'];

function ContextDiagram({ ruleKey, size = 54 }: { ruleKey: string; size?: number }) {
  const ref = useCanvas(
    [size, size],
    (ctx) => {
      const { own, nb } = decodeKey(ruleKey);
      const c = size / 3;
      const cell = (gx: number, gy: number, fill: string, poly: [number, number][]) => {
        if (!poly.length) return;
        ctx.fillStyle = fill;
        ctx.beginPath();
        poly.forEach(([x, y], i) => {
          const px = (gx + 1 + x) * c, py = (2 - (gy + 1) + 1 - y) * c;
          if (i) ctx.lineTo(px, py);
          else ctx.moveTo(px, py);
        });
        ctx.closePath();
        ctx.fill();
      };
      ctx.fillStyle = '#20232c';
      ctx.fillRect(0, 0, size, size);
      for (let i = 0; i < 8; i++) {
        const [gx, gy] = NB_POS[i];
        const v = nb[i];
        const known = v.shape !== undefined || v.contact !== undefined || v.corner !== undefined;
        if (!known) {
          ctx.fillStyle = '#2c303b';
          ctx.fillRect((gx + 1) * c + 1, (1 - gy) * c + 1, c - 2, c - 2);
          ctx.strokeStyle = '#3b404d';
          ctx.beginPath();
          ctx.moveTo((gx + 1) * c + 2, (1 - gy) * c + c - 2);
          ctx.lineTo((gx + 1) * c + c - 2, (1 - gy) * c + 2);
          ctx.stroke();
          continue;
        }
        if (v.shape !== undefined) cell(gx, gy, '#8a93a8', shapePoly(v.shape));
        if (v.contact) {
          const t = v.contact === 2 ? 1 : 0.5;
          // strip along the side shared with the centre cell
          const d = 0.35;
          const poly: [number, number][] =
            i === 0 ? [[1 - d, 0.5 - t / 2], [1, 0.5 - t / 2], [1, 0.5 + t / 2], [1 - d, 0.5 + t / 2]]
            : i === 1 ? [[0, 0.5 - t / 2], [d, 0.5 - t / 2], [d, 0.5 + t / 2], [0, 0.5 + t / 2]]
            : i === 2 ? [[0.5 - t / 2, 1 - d], [0.5 + t / 2, 1 - d], [0.5 + t / 2, 1], [0.5 - t / 2, 1]]
            : [[0.5 - t / 2, 0], [0.5 + t / 2, 0], [0.5 + t / 2, d], [0.5 - t / 2, d]];
          cell(gx, gy, '#8a93a8', poly);
        }
        if (v.corner) {
          const q = 0.4;
          const cx = gx < 0 ? 1 - q : 0, cy = gy < 0 ? 1 - q : 0;
          cell(gx, gy, '#8a93a8', [[cx, cy], [cx + q, cy], [cx + q, cy + q], [cx, cy + q]]);
        }
      }
      cell(0, 0, '#e0b25a', shapePoly(own));
      ctx.strokeStyle = '#4a5060';
      ctx.lineWidth = 1;
      for (let i = 0; i <= 3; i++) {
        ctx.beginPath();
        ctx.moveTo(i * c + 0.5, 0);
        ctx.lineTo(i * c + 0.5, size);
        ctx.moveTo(0, i * c + 0.5);
        ctx.lineTo(size, i * c + 0.5);
        ctx.stroke();
      }
    },
    [ruleKey],
  );
  return h('canvas', { ref, width: size, height: size, className: 'tt-diagram', title: KEY_LEVEL_NAMES[decodeKey(ruleKey).level] });
}

// ------------------------------------------------------------------ rule editor

function learnedRules(d: TerrainStyleData, layer: RuleLayer): Record<string, number[]> {
  return layer === 'tones' ? (d.tones?.rules ?? {}) : d[layer].rules;
}

function RuleBrowser({ d, ov, layer, variant, update, pick }: { d: TerrainStyleData; ov: StyleOverride; layer: RuleLayer; variant: number; update: (fn: (o: StyleOverride) => void) => void; pick: (cb: (choice: number) => void, opts: { fill: boolean; none: boolean }) => void }) {
  const [level, setLevel] = useState(0);
  const [query, setQuery] = useState('');
  const [onlyEdited, setOnlyEdited] = useState(false);
  const [limit, setLimit] = useState(PAGE);
  const table = spriteTable(d, ov);
  const learned = learnedRules(d, layer);
  const edited = ov.rules?.[layer] ?? {};
  const entries = useMemo(() => {
    const merged: Record<string, number[]> = { ...learned, ...edited };
    const q = query.trim().toLowerCase();
    return Object.entries(merged)
      .filter(([k]) => k.charCodeAt(0) - 97 === level)
      // fully enclosed cells never consult edge rules (they are interior fill)
      .filter(([k]) => layer === 'tones' || !INTERIOR_KEYS.has(k))
      .filter(([k]) => !onlyEdited || k in edited)
      .filter(([, arr]) => !q || pairs(arr).some(([c]) => choiceLabel(table, c).toLowerCase().includes(q)))
      .map(([k, arr]) => ({ k, arr, n: pairs(learned[k]).reduce((a, [, w]) => a + w, 0) }))
      .sort((a, b) => b.n - a.n || a.k.localeCompare(b.k));
  }, [learned, edited, level, query, onlyEdited, table]);
  useEffect(() => setLimit(PAGE), [level, query, onlyEdited, layer]);
  const setRule = (k: string, arr: number[]) =>
    update((o) => {
      o.rules ??= {};
      const r = (o.rules[layer] ??= {});
      if (learned[k] && learned[k].join(',') === arr.join(',')) delete r[k];
      else r[k] = arr;
    });
  return h(
    'div',
    { className: 'tt-rules' },
    h(
      'div',
      { className: 'tt-row wrap' },
      h(
        'div',
        { className: 'tt-seg' },
        KEY_LEVEL_NAMES.map((n, i) => h('button', { key: n, className: level === i ? 'active' : '', onClick: () => setLevel(i) }, n)),
      ),
      h('input', { className: 'tt-search', placeholder: 'filter by sprite…', value: query, onChange: (e: { target: HTMLInputElement }) => setQuery(e.target.value) }),
      h(Toggle, { value: onlyEdited, onChange: setOnlyEdited, label: 'edited only' }),
      h('span', { className: 'tt-dim' }, `${entries.length} contexts`),
    ),
    h(
      'p',
      { className: 'tt-help' },
      'Each card is a neighbourhood context (orange = the cell, grey = known neighbours, hatched = any) and the weighted sprites chosen for it. ',
      'The generator uses the most specific context with enough observations (mirror images are used when only the other orientation was seen) and smooths choices with neighbour statistics.',
    ),
    h(
      'div',
      { className: 'tt-cards' },
      entries.slice(0, limit).map(({ k, arr, n }) =>
        h(
          'div',
          { key: k, className: `tt-card ${k in edited ? 'edited' : ''}` },
          h('div', { className: 'tt-card-head' }, h(ContextDiagram, { ruleKey: k }), h('div', { className: 'tt-dim small' }, `${n} seen`, h('br'), k)),
          h(ChoiceList, {
            d,
            table,
            variant,
            arr,
            learned: learned[k],
            onChange: (a) => setRule(k, a),
            onAdd: () => pick((c) => setRule(k, [...arr, c, Math.max(1, Math.round(n / 4))]), { fill: layer === 'level', none: layer !== 'level' }),
          }),
        ),
      ),
    ),
    entries.length > limit && h('div', { className: 'tt-row center' }, h(Btn, { onClick: () => setLimit(limit + PAGE) }, `Show more (${entries.length - limit})`)),
  );
}

// ------------------------------------------------------------------ preview

function previewShapes(dirs: Directions): Vec2[][] {
  const ring: Vec2[] = [[0, 0], [40, 0], [40, 13]];
  if (dirs.s45) ring.push([34, 13], [29, 8]);
  else ring.push([34, 13], [34, 8], [29, 8]);
  if (dirs.s21) ring.push([23, 8], [17, 11]);
  else ring.push([23, 8], [23, 11], [17, 11]);
  if (dirs.s12) ring.push([12, 11], [10, 7]);
  else ring.push([12, 11], [12, 7], [10, 7]);
  ring.push([4, 7], [4, 10], [0, 10]);
  return [ring];
}

function Preview({ styleId, variant, version, d }: { styleId: string; variant: number; version: number; d: TerrainStyleData }) {
  const W = 46, H = 22, S = 16;
  const ref = useCanvas(
    [W * S, H * S],
    (ctx, redraw) => {
      const dirs = d.directions;
      const layers: TileLayer[] = [
        { id: 'bg', name: 'Background', role: 'background', sortingLayer: 'Background', sortingOrder: 0, tiles: [] } as unknown as TileLayer,
        { id: 'lvl', name: 'Level', role: 'level', sortingLayer: 'Level', sortingOrder: 0, tiles: [] } as unknown as TileLayer,
        { id: 'fg', name: 'Foreground', role: 'foreground', sortingLayer: 'Foreground', sortingOrder: 0, tiles: [] } as unknown as TileLayer,
      ];
      const ov = styleOverride(styleId) ?? {};
      const shading: ShadingSettings = { ...(ov.defaultShading ?? d.defaultShading) };
      const decoration: DecorationSettings = { ...(ov.defaultDecoration ?? d.defaultDecoration) };
      const base = { style: styleId, variant: d.variants[variant]?.name, shading, decoration, targets: { level: 'lvl', foreground: 'fg', background: 'bg' } };
      const terrains: TerrainShape[] = [
        { ...base, id: 'p0', mode: 'add', rings: previewShapes(dirs) },
        { ...base, id: 'p1', mode: 'add', rings: [[[5, 15], [14, 15], [14, 18], [5, 18]]] },
        { ...base, id: 'p2', mode: 'subtract', rings: [[[27, 2], [33, 2], [33, 5], [27, 5]]] },
      ];
      const doc = { palette: [] as PaletteEntry[], layers, terrains, bodies: [], objects: [] } as unknown as LevelDoc;
      const out = generateTerrain(doc, { layers, terrains });
      ctx.fillStyle = '#272a3a';
      ctx.fillRect(0, 0, W * S, H * S);
      for (const id of ['bg', 'lvl', 'fg']) {
        const t = out.get(id) ?? [];
        for (let i = 0; i < t.length; i += 4) {
          ctx.setTransform(S, 0, 0, -S, (t[i] + 3 + 0.5) * S, (H - 2 - t[i + 1] - 0.5) * S);
          drawSpriteAt(ctx, doc.palette[t[i + 2]], t[i + 3], redraw);
        }
      }
    },
    [styleId, variant, version],
  );
  return h('canvas', { ref, width: W * S, height: H * S, className: 'tt-preview' });
}

// ------------------------------------------------------------------ tabs

function FillTab({ d, ov, variant, update, pick }: { d: TerrainStyleData; ov: StyleOverride; variant: number; update: (fn: (o: StyleOverride) => void) => void; pick: (cb: (choice: number) => void, opts: { fill: boolean; none: boolean }) => void }) {
  const table = spriteTable(d, ov);
  const arr = ov.fill ?? d.fill;
  const set = (a: number[]) =>
    update((o) => {
      if (a.join(',') === d.fill.join(',')) delete o.fill;
      else o.fill = a;
    });
  return h(
    'div',
    null,
    h('p', { className: 'tt-help' }, d.fill.length > 2 ? 'Interior cells pick from these sprites; neighbour statistics from the levels keep the learned pattern (e.g. a repeating brick layout).' : 'Interior cells use these sprites (weighted at random).'),
    h(ChoiceList, { d, table, variant, arr, learned: d.fill, onChange: set, onAdd: () => pick((c) => set([...arr, c, 10]), { fill: false, none: false }) }),
  );
}

function TonesTab({ d, ov, variant, update, pick }: { d: TerrainStyleData; ov: StyleOverride; variant: number; update: (fn: (o: StyleOverride) => void) => void; pick: (cb: (choice: number) => void, opts: { fill: boolean; none: boolean }) => void }) {
  const table = spriteTable(d, ov);
  if (!d.tones) return h('p', { className: 'tt-help' }, 'This art has no dark interior tones.');
  return h(
    'div',
    null,
    h('p', { className: 'tt-help' }, `Dark shading: base tone luminance ${d.tones.base}, dark tone ${d.tones.dark}. Set a weight to 0 to never use a tone tile.`),
    h(
      'div',
      { className: 'tt-tones' },
      d.tones.tiles.map((t, i) => {
        const w = ov.toneWeights?.[i] ?? t.w;
        return h(
          'div',
          { key: i, className: `tt-choice ${w <= 0 ? 'off' : ''}`, title: choiceLabel(table, t.c) },
          h(ChoiceThumb, { d, table, choice: t.c, variant, size: 44 }),
          h(Num, {
            value: w,
            min: 0,
            onChange: (v) =>
              update((o) => {
                o.toneWeights ??= {};
                if (v === t.w) delete o.toneWeights[i];
                else o.toneWeights[i] = Math.max(0, v);
              }),
          }),
        );
      }),
    ),
    h('h3', null, 'Tone rules'),
    h(RuleBrowser, { d, ov, layer: 'tones', variant, update, pick }),
  );
}

function StampView({ d, table, s, variant }: { d: TerrainStyleData; table: PaletteEntry[]; s: Stamp; variant: number }) {
  let x0 = 0, y0 = 0, x1 = 0, y1 = 0;
  const grow = (x: number, y: number) => {
    x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y);
  };
  for (let j = 0; j < s.tiles.length; j += 3) grow(s.tiles[j], s.tiles[j + 1]);
  for (let j = 0; j < s.req.length; j += 4) if (s.req[j + 3] !== 2 || Math.abs(s.req[j]) + Math.abs(s.req[j + 1]) <= 3) grow(s.req[j], s.req[j + 1]);
  const cw = x1 - x0 + 1, ch = y1 - y0 + 1;
  const cs = Math.max(8, Math.min(24, Math.floor(150 / Math.max(cw, ch))));
  const ref = useCanvas(
    [cw * cs, ch * cs],
    (ctx, redraw) => {
      ctx.fillStyle = '#20232c';
      ctx.fillRect(0, 0, cw * cs, ch * cs);
      for (let j = 0; j < s.req.length; j += 4) {
        const [x, y, sh, mode] = [s.req[j] - x0, s.req[j + 1] - y0, s.req[j + 2], s.req[j + 3]];
        if (x < 0 || y < 0 || x >= cw || y >= ch) continue;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        if (mode === 1) {
          ctx.strokeStyle = '#4b5263';
          ctx.setLineDash([2, 2]);
          ctx.strokeRect(x * cs + 0.5, (ch - 1 - y) * cs + 0.5, cs - 1, cs - 1);
          ctx.setLineDash([]);
          continue;
        }
        const poly = shapePoly(sh);
        if (!poly.length) continue;
        ctx.fillStyle = mode === 0 ? '#5a6275' : '#454b5a';
        ctx.beginPath();
        poly.forEach(([px, py], i) => (i ? ctx.lineTo((x + px) * cs, (ch - y - py) * cs) : ctx.moveTo((x + px) * cs, (ch - y - py) * cs)));
        ctx.fill();
      }
      for (let j = 0; j < s.tiles.length; j += 3) {
        const c = s.tiles[j + 2];
        if (c < 0) continue;
        const pe = variantPe(d, table, c >> 3, variant);
        if (!pe) continue;
        ctx.setTransform(cs, 0, 0, -cs, (s.tiles[j] - x0 + 0.5) * cs, (ch - (s.tiles[j + 1] - y0) - 0.5) * cs);
        drawSpriteAt(ctx, pe, c & 7, redraw);
      }
    },
    [s, variant],
  );
  return h('canvas', { ref, width: cw * cs, height: ch * cs, className: 'tt-stamp' });
}

function DecorTab({ d, ov, variant, update }: { d: TerrainStyleData; ov: StyleOverride; variant: number; update: (fn: (o: StyleOverride) => void) => void }) {
  const table = spriteTable(d, ov);
  const [layer, setLayer] = useState<'all' | 'level' | 'fg' | 'bg'>('all');
  const [kind, setKind] = useState<'all' | FoliageKind>('all');
  const [limit, setLimit] = useState(PAGE);
  const kindOf = (s: Stamp, i: number): FoliageKind => ov.stamps?.[i]?.kind ?? s.kind ?? 'props';
  const list = useMemo(
    () =>
      d.stamps
        .map((s, i) => ({ s, i }))
        .filter(({ s, i }) => (layer === 'all' || s.layer === layer) && (kind === 'all' || kindOf(s, i) === kind))
        .sort((a, b) => b.s.n - a.s.n || b.s.rate - a.s.rate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [d, layer, kind, ov],
  );
  return h(
    'div',
    null,
    h(
      'div',
      { className: 'tt-row wrap' },
      h('div', { className: 'tt-seg' }, (['all', 'level', 'fg', 'bg'] as const).map((l) => h('button', { key: l, className: layer === l ? 'active' : '', onClick: () => setLayer(l) }, l === 'fg' ? 'foreground' : l === 'bg' ? 'background' : l))),
      h(
        'div',
        { className: 'tt-seg' },
        (['all', ...FOLIAGE_KINDS] as const).map((k) => h('button', { key: k, className: kind === k ? 'active' : '', onClick: () => setKind(k) }, k === 'all' ? 'any kind' : FOLIAGE_LABELS[k].toLowerCase())),
      ),
      h('span', { className: 'tt-dim' }, `${list.length} decorations`),
    ),
    h(
      'p',
      { className: 'tt-help' },
      'Decorations learned from the levels with the terrain they need (dark grey = required shape, dotted = must stay empty). The chance is per eligible cell, multiplied by the shape\'s density and the density of its kind. The kind also decides which foliage brush paints it.',
    ),
    h(
      'div',
      { className: 'tt-cards' },
      list.slice(0, limit).map(({ s, i }) => {
        const o = ov.stamps?.[i];
        const rate = o?.rate ?? s.rate;
        const off = o?.off ?? s.off ?? false;
        const set = (patch: { rate?: number; off?: boolean; kind?: FoliageKind }) =>
          update((ov2) => {
            ov2.stamps ??= {};
            const cur = { ...(ov2.stamps[i] ?? {}), ...patch };
            if (cur.rate === s.rate) delete cur.rate;
            if (cur.off === (s.off ?? false)) delete cur.off;
            if (cur.kind === (s.kind ?? 'props')) delete cur.kind;
            if (cur.rate === undefined && cur.off === undefined && cur.kind === undefined) delete ov2.stamps[i];
            else ov2.stamps[i] = cur;
          });
        return h(
          'div',
          { key: i, className: `tt-card stamp ${off ? 'off' : ''} ${o ? 'edited' : ''}` },
          h(StampView, { d, table, s, variant }),
          h(
            'div',
            { className: 'tt-col' },
            h('div', { className: 'tt-dim small' }, `${s.layer === 'fg' ? 'foreground' : s.layer === 'bg' ? 'background' : 'level'} · seen ${s.n}×`),
            h('label', { className: 'tt-row' }, 'chance %', h(Num, { value: Math.round(rate * 10000) / 100, step: 0.1, min: 0, max: 100, onChange: (v) => set({ rate: Math.max(0, v) / 100 }) })),
            h(
              'label',
              { className: 'tt-row' },
              'kind',
              h(
                'select',
                { value: kindOf(s, i), onChange: (e: { target: { value: string } }) => set({ kind: e.target.value as FoliageKind }) },
                FOLIAGE_KINDS.map((k) => h('option', { key: k, value: k }, FOLIAGE_LABELS[k])),
              ),
            ),
            h(Toggle, { value: !off, onChange: (v) => set({ off: !v }), label: 'enabled' }),
          ),
        );
      }),
    ),
    list.length > limit && h('div', { className: 'tt-row center' }, h(Btn, { onClick: () => setLimit(limit + PAGE) }, `Show more (${list.length - limit})`)),
  );
}

function SettingsTab({ d, ov, update }: { d: TerrainStyleData; ov: StyleOverride; update: (fn: (o: StyleOverride) => void) => void }) {
  const dirs = ov.directions ?? ALL_DIRECTIONS;
  const sh = ov.defaultShading ?? d.defaultShading;
  const de = ov.defaultDecoration ?? d.defaultDecoration;
  const setDir = (k: keyof Directions, v: boolean) =>
    update((o) => {
      const n = { ...dirs, [k]: v };
      if (n.s45 && n.s21 && n.s12) delete o.directions;
      else o.directions = n;
    });
  const setSh = (p: Partial<ShadingSettings>) => update((o) => void (o.defaultShading = { ...sh, ...p }));
  const setDe = (p: Partial<DecorationSettings>) => update((o) => void (o.defaultDecoration = { ...de, ...p }));
  const learnedDir = (k: keyof Directions) => (d.directions[k] ? 'in the art' : 'not in the art: cells are marked as missing tiles');
  return h(
    'div',
    { className: 'tt-settings' },
    h('h3', null, 'Name'),
    h('input', { className: 'tt-search', value: ov.name ?? d.name, onChange: (e: { target: HTMLInputElement }) => update((o) => void (e.target.value === d.name ? delete o.name : (o.name = e.target.value))) }),
    h('h3', null, 'Slope directions'),
    h('p', { className: 'tt-help' }, 'Polygon edges the style can draw (straight edges always work). Slopes the art has no pieces for use the closest learned pieces and are marked as missing tiles in the editor until you link or draw them (Tile cases tab, or the Fix tool).'),
    h(
      'div',
      { className: 'tt-col' },
      h('div', { className: 'tt-row' }, h(Toggle, { value: dirs.s45, onChange: (v) => setDir('s45', v), label: '45° (1:1)' }), h('span', { className: 'tt-dim small' }, learnedDir('s45'))),
      h('div', { className: 'tt-row' }, h(Toggle, { value: dirs.s21, onChange: (v) => setDir('s21', v), label: 'shallow 2:1' }), h('span', { className: 'tt-dim small' }, learnedDir('s21'))),
      h('div', { className: 'tt-row' }, h(Toggle, { value: dirs.s12, onChange: (v) => setDir('s12', v), label: 'steep 1:2' }), h('span', { className: 'tt-dim small' }, learnedDir('s12'))),
    ),
    h('h3', null, 'Default dark shading (new shapes)'),
    !d.tones && h('p', { className: 'tt-help' }, 'No dark tones in this art.'),
    d.tones &&
      h(
        'div',
        { className: 'tt-col' },
        h(Toggle, { value: sh.enabled, onChange: (v) => setSh({ enabled: v }), label: 'enabled' }),
        h('label', { className: 'tt-row' }, 'depth (cells)', h(Num, { value: sh.depth, step: 1, min: 1, onChange: (v) => setSh({ depth: v }) })),
        h('label', { className: 'tt-row' }, 'jitter (0..1)', h(Num, { value: sh.jitter, step: 0.1, min: 0, max: 1, onChange: (v) => setSh({ jitter: v }) })),
        h('label', { className: 'tt-row' }, 'seed', h(Num, { value: sh.seed, step: 1, onChange: (v) => setSh({ seed: Math.round(v) }) })),
      ),
    h('h3', null, 'Default decoration (new shapes)'),
    h(
      'div',
      { className: 'tt-col' },
      h(Toggle, { value: de.enabled, onChange: (v) => setDe({ enabled: v }), label: 'enabled' }),
      h('label', { className: 'tt-row' }, 'density', h(Num, { value: de.density, step: 0.1, min: 0, onChange: (v) => setDe({ density: v }) })),
      h('label', { className: 'tt-row' }, 'seed', h(Num, { value: de.seed, step: 1, onChange: (v) => setDe({ seed: Math.round(v) }) })),
      h(Toggle, { value: de.foreground, onChange: (v) => setDe({ foreground: v }), label: 'foreground decorations' }),
      h(Toggle, { value: de.background, onChange: (v) => setDe({ background: v }), label: 'background decorations' }),
    ),
    d.stats && h('p', { className: 'tt-help' }, `Learned from ${d.stats.levels.length} levels (${d.stats.cells} terrain cells): ${d.stats.levels.join(', ')}.`),
  );
}

// ------------------------------------------------------------------ main component


export function TilesetTool({ onClose, initialStyle }: { onClose: () => void; initialStyle?: string }) {
  const data = terrainData();
  const styles = data?.styles ?? [];
  const [styleId, setStyleId] = useState((styles.find((s) => s.id === initialStyle) ?? styles[0])?.id ?? '');
  const d = styleData(styleId);
  const [ov, setOv] = useState<StyleOverride>(() => structuredClone(styleOverride(styleId) ?? {}));
  const saved = useRef<StyleOverride | null>(styleOverride(styleId));
  const [dirty, setDirty] = useState(false);
  const [tab, setTab] = useState<Tab>('cases');
  const [layer, setLayer] = useState<'level' | 'fg'>('level');
  const [variant, setVariant] = useState(0);
  const [version, setVersion] = useState(0);
  const [status, setStatus] = useState('');
  const [picker, setPicker] = useState<null | { cb: (c: number) => void; fill: boolean; none: boolean }>(null);

  useEffect(() => onTerrainStylesChanged(() => setVersion((v) => v + 1)), []);

  const confirmDiscard = () => !dirty || window.confirm('Discard unsaved changes to this style?');

  const restoreSaved = () => {
    if (dirty) setStyleOverride(styleId, saved.current);
  };

  const switchStyle = (id: string) => {
    if (id === styleId || !confirmDiscard()) return;
    restoreSaved();
    setStyleId(id);
    const o = styleOverride(id);
    saved.current = o;
    setOv(structuredClone(o ?? {}));
    setDirty(false);
    setVariant(0);
    setStatus('');
  };

  const update = (fn: (o: StyleOverride) => void) => {
    const n = structuredClone(ov);
    fn(n);
    setOv(n);
    setStyleOverride(styleId, n);
    setDirty(true);
    setStatus('');
  };

  const save = async () => {
    setStatus('saving…');
    try {
      await saveAsset('terrain-style', styleId, ov);
      saved.current = structuredClone(ov);
      setDirty(false);
      setStatus('saved');
    } catch (e) {
      setStatus('save failed: ' + String(e));
    }
  };

  const revertAll = () => {
    if (!window.confirm('Reset all edits of this style to the learned data?')) return;
    update((o) => {
      for (const k of Object.keys(o)) delete (o as Record<string, unknown>)[k];
    });
  };

  const close = () => {
    if (!confirmDiscard()) return;
    restoreSaved();
    onClose();
  };

  const pick = (cb: (c: number) => void, opts: { fill: boolean; none: boolean }) => setPicker({ cb, ...opts });

  const onPickSprite = (pe: PaletteEntry, flags: number) => {
    if (!d || !picker) return;
    const table = spriteTable(d, ov);
    let si = table.findIndex((p) => p.tex === pe.tex && p.sprite === pe.sprite);
    const cb = picker.cb;
    setPicker(null);
    if (si < 0) {
      si = table.length;
      const n = structuredClone(ov);
      n.extraSprites = [...(n.extraSprites ?? []), pe.tile ? { tex: pe.tex, sprite: pe.sprite, tile: pe.tile } : { tex: pe.tex, sprite: pe.sprite }];
      // apply the sprite table extension first so the callback's update sees it
      setOv(n);
      setStyleOverride(styleId, n);
      ovRef.current = n;
    }
    cb(si * 8 + flags);
  };
  const ovRef = useRef(ov);
  ovRef.current = ov;

  if (!data || !d) {
    return h(
      'div',
      { className: 'tt-root' },
      h('style', null, TT_CSS),
      h('div', { className: 'tt-head' }, h('h2', null, 'Terrain styles'), h('span', { className: 'tt-grow' }), h(Btn, { onClick: onClose }, 'Close')),
      h('div', { className: 'tt-body' }, h('p', null, 'Terrain style data is not loaded (public/game-data/terrain.json).')),
    );
  }

  // updates issued right after adding an extra sprite must build on the extended override
  const updateLatest = (fn: (o: StyleOverride) => void) => {
    const n = structuredClone(ovRef.current);
    fn(n);
    ovRef.current = n;
    setOv(n);
    setStyleOverride(styleId, n);
    setDirty(true);
    setStatus('');
  };

  const editProps = { d, ov, variant, update: updateLatest, pick };

  return h(
    'div',
    { className: 'tt-root' },
    h('style', null, TT_CSS),
    h(
      'div',
      { className: 'tt-head' },
      h('h2', null, 'Terrain styles'),
      h(
        'select',
        { className: 'tt-select', value: styleId, onChange: (e: { target: HTMLSelectElement }) => switchStyle(e.target.value) },
        styles.map((s) => h('option', { key: s.id, value: s.id }, styleOverride(s.id)?.name ?? s.name)),
      ),
      d.variants.length > 1 &&
        h(
          'select',
          { className: 'tt-select', value: variant, title: 'colour variant (for display)', onChange: (e: { target: HTMLSelectElement }) => setVariant(+e.target.value) },
          d.variants.map((v, i) => h('option', { key: v.name, value: i }, v.name)),
        ),
      h('span', { className: 'tt-grow' }),
      status && h('span', { className: 'tt-status' }, status),
      h(Btn, { onClick: revertAll, kind: 'ghost', title: 'drop all edits of this style' }, 'Reset to learned'),
      h(Btn, { onClick: save, kind: 'primary', disabled: !dirty }, dirty ? 'Save' : 'Saved'),
      h(Btn, { onClick: close }, 'Close'),
    ),
    h(
      'div',
      { className: 'tt-preview-wrap' },
      h(Preview, { styleId, variant, version, d }),
      h(
        'div',
        { className: 'tt-col', style: { maxWidth: 420 } },
        h('div', { className: 'tt-dim small' }, 'Live preview with the current edits (default shading and decoration of this style).'),
        h(
          'div',
          { className: 'tt-dim small' },
          `${Object.keys(d.level.rules).length} level contexts · ${Object.keys(d.fg.rules).length} foreground contexts · ${d.stamps.length} decorations` + (d.tones ? ` · ${d.tones.tiles.length} tone tiles` : ''),
        ),
      ),
    ),
    h(
      'div',
      { className: 'tt-tabs' },
      ([
        ['cases', 'Tile cases'],
        ['rules', 'Edge rules'],
        ['fill', 'Fill'],
        ['tones', 'Dark tones'],
        ['decor', 'Decorations'],
        ['settings', 'Directions & defaults'],
      ] as [Tab, string][]).map(([t, label]) => h('button', { key: t, className: tab === t ? 'active' : '', onClick: () => setTab(t) }, label)),
    ),
    h(
      'div',
      { className: 'tt-body' },
      tab === 'rules' &&
        h(
          Fragment,
          null,
          h(
            'div',
            { className: 'tt-row wrap' },
            h(
              'div',
              { className: 'tt-seg' },
              h('button', { className: layer === 'level' ? 'active' : '', onClick: () => setLayer('level') }, 'Level layer'),
              h('button', { className: layer === 'fg' ? 'active' : '', onClick: () => setLayer('fg') }, 'Foreground edge art'),
            ),
          ),
          h(RuleBrowser, { key: layer, ...editProps, layer }),
        ),
      tab === 'cases' && h(CasesTab, { ...editProps, styleId, key: styleId }),
      tab === 'fill' && h(FillTab, editProps),
      tab === 'tones' && h(TonesTab, editProps),
      tab === 'decor' && h(DecorTab, editProps),
      tab === 'settings' && h(SettingsTab, editProps),
    ),
    picker &&
      h(SpritePicker, {
        d,
        table: spriteTable(d, ov),
        variant,
        allowFill: picker.fill,
        allowNone: picker.none,
        onPick: onPickSprite,
        onPickSpecial: (c: number) => {
          picker.cb(c);
          setPicker(null);
        },
        onClose: () => setPicker(null),
      }),
  );
}
